<template>
  <v-container :class="containerClass" fluid>
    <v-row align="baseline">
      <v-col cols="12" md="3">
        <div class="label mb-2">Student</div>
        <v-autocomplete
          v-model="filters.student_id"
          :items="students"
          item-text="name"
          item-value="id"
          :loading="isStudentListLoading"
          :search-input.sync="searchInputStudent"
          outlined
          solo
          flat
          background-color="white"
          label="Student"
          multiple
          hide-details
          dense
          clearable
          cache-items
          @change="getListData"
          @click:clear="getListData"
        />
      </v-col>
      <v-col cols="12" md="2">
        <div class="label mb-2">Issue Date</div>
        <date-picker
          v-model="filters.issue_date"
          value-type="format"
          type="date"
          range
          class="mx-datepicker--dense mb-0"
          confirm
          @confirm="getListData"
          @clear="getListData"
        ></date-picker>
      </v-col>
      <v-col cols="12" md="2">
        <div class="label mb-2">Invoice Status</div>
        <v-select
          v-model="filters.invoice_status"
          :items="invoiceStatusOptions"
          outlined
          solo
          flat
          background-color="white"
          label="Invoice Status"
          hide-details
          dense
          clearable
          @change="getListData"
          @click:clear="getListData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div class="overflow-x-auto">
          <table class="report-table bordered full-width">
            <thead>
              <tr>
                <th rowspan="2">Student</th>
                <th rowspan="2">Subscription Order</th>
                <th
                  v-for="term in totalTerms"
                  :key="term"
                  class="text-center"
                  :colspan="monthsPerTerm"
                >
                  Term {{ term }}
                </th>
              </tr>
              <tr>
                <th v-for="month in totalMonths" :key="month">
                  Bulan {{ month }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in items" :key="i">
                <th style="min-width: 100px">{{ item.student_name }}</th>
                <th>
                  <v-btn
                    v-if="item.order_id === null"
                    color="primary"
                    small
                    target="blank"
                    :href="
                      $route('subscription-orders.create', {
                        subscription_package_id: item.package_id,
                        student_id: item.student_id,
                      })
                    "
                  >
                    Create Order
                  </v-btn>
                  <a
                    v-else
                    target="_blank"
                    :href="$route('subscription-orders.update', item.order_id)"
                  >
                    {{ item.order_code }} - {{ item.package_name }}
                  </a>
                </th>
                <td v-for="month in totalMonths" :key="month">
                  <div v-if="isInvoicesAvailable(item.invoices, month)">
                    <v-alert
                      v-for="(invoice, j) in findInvoices(item.invoices, month)"
                      :key="j"
                      :color="invoiceStatusProps(invoice.status, 'color')"
                      dark
                      dense
                      class="ma-1 text-center"
                    >
                      <a
                        :href="$route('invoices.update', invoice.id)"
                        class="text-decoration-none white--text text-subtitle-2"
                      >
                        {{ invoice.code }} <br />
                        ({{ invoiceStatusProps(invoice.status, 'text') }})
                      </a>
                    </v-alert>
                  </div>
                  <v-btn
                    v-else
                    color="primary"
                    small
                    target="blank"
                    :href="
                      $route('invoices.create', {
                        subscription_order_id: item.order_id,
                        term: Math.ceil(month / monthsPerTerm),
                        month,
                      })
                    "
                  >
                    Create Invoice
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-row align="center" justify="center">
          <v-col cols="12" md="auto">
            <v-select
              v-model="options.itemsPerPage"
              :items="rowsPerPageOptions"
              outlined
              dense
              label="Rows per page"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="auto">
            <v-pagination
              v-model="options.page"
              :length="itemList.meta.last_page"
              :total-visible="10"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import { mapGetters, mapActions } from 'vuex'
import { ListParams } from '@/models'
import utils from '@/utils'
import constant from '@/constant'

export default {
  name: 'InvoiceMonitoring',
  layout: LayoutDashboard,
  data() {
    return {
      monthsPerTerm: 3,
      totalTerms: 50,
      options: {
        page: 1,
        itemsPerPage: 25,
      },
      filters: {
        student_id: [],
        issue_date: [],
        invoice_status: null,
      },
      searchInputStudent: null,
    }
  },
  computed: {
    ...mapGetters({
      itemList: 'report/invoiceMonitoring/list',
      isItemListLoading: 'report/invoiceMonitoring/isListLoading',
      studentList: 'student/searchList',
      isStudentListLoading: 'student/isSearchListLoading',
    }),
    totalMonths() {
      return this.totalTerms * this.monthsPerTerm
    },
    items() {
      return this.itemList.data
    },
    students() {
      return this.studentList.data
    },
    invoiceStatusOptions() {
      return constant.invoiceStatusOptions
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getListData()
      },
    },
    searchInputStudent(val) {
      if (val) {
        this.debouncedSearchStudentList()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchStudentList = _.debounce(this.searchStudentList, 500)
  },
  methods: {
    ...mapActions({
      getItemList: 'report/invoiceMonitoring/getList',
      getStudentList: 'student/search',
    }),
    async init() {
      await this.getListData()
    },
    async getListData() {
      const listParams = new ListParams(this.filters, this.options)
      await this.getItemList(utils.toServerSideQuery(listParams))
    },
    isInvoicesAvailable(invoices, month) {
      const result = this.findInvoices(invoices, month)
      return result !== null && result.length > 0
    },
    findInvoices(invoices, month) {
      if (!invoices || invoices.length === 0) {
        return null
      }
      return invoices.filter(
        (e) =>
          e.term === Math.ceil(month / this.monthsPerTerm) && e.month === month
      )
    },
    searchStudentList() {
      this.getStudentList({ search: this.searchInputStudent })
    },
  },
}
</script>
<style lang="scss" scoped>
table thead tr:first-child th:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 2;
}

table thead tr:first-child th:nth-child(2) {
  position: sticky;
  left: 100px;
  background: white;
  z-index: 2;
}

table tbody th:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

table tbody th:nth-child(2) {
  position: sticky;
  left: 100px;
  background: white;
  z-index: 1;
}
</style>
